/**
 * Drafts: what you have collected for a card that is not resolved yet.
 *
 * The capture screen used to hold the photos, the recording and the note in
 * component state, so walking back to the hand to look at another card threw them
 * away -- and so did the phone killing a backgrounded PWA. Priority (1) of the
 * brief is that a photo is never lost, and a photo you have already taken counts.
 * So a draft is parked here, keyed by card instance id, until the card is played.
 *
 * IndexedDB rather than localStorage: a File survives the structured clone intact,
 * name and mime type included, where localStorage would need a base64 string that a
 * phone photo does not fit into anyway.
 *
 * Every call degrades to a no-op when the store cannot be opened -- a private
 * window, storage denied, an old browser. A draft that cannot be saved must never
 * stop a card being played, so nothing here ever rejects.
 */

const DB_NAME = "conquest-drafts";
const DB_VERSION = 1;
const STORE = "cards";

export interface CardDraft {
  photos: File[];
  audio: Blob | null;
  note: string;
}

interface StoredDraft extends CardDraft {
  cardId: number;
  updatedAt: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "cardId" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function run<T>(
  mode: IDBTransactionMode,
  request: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        try {
          const tx = db.transaction(STORE, mode);
          const req = request(tx.objectStore(STORE));
          req.onsuccess = () => resolve(req.result as T);
          req.onerror = () => resolve(null);
          tx.onabort = () => resolve(null);
        } catch {
          resolve(null);
        }
      }),
  );
}

export function loadDraft(cardId: number): Promise<CardDraft | null> {
  return run<StoredDraft | undefined>("readonly", (store) => store.get(cardId)).then((record) => {
    if (!record) return null;
    return {
      // Defensive: a record written by an older build, or a half-cleared store,
      // must not crash the capture screen.
      photos: Array.isArray(record.photos) ? record.photos : [],
      audio: record.audio ?? null,
      note: typeof record.note === "string" ? record.note : "",
    };
  });
}

/** Nothing worth keeping. Opening a card and leaving it alone produces one of these. */
export function isEmptyDraft(draft: CardDraft): boolean {
  return draft.photos.length === 0 && !draft.audio && !draft.note.trim();
}

export function saveDraft(cardId: number, draft: CardDraft): Promise<unknown> {
  // An empty draft is not worth a row, and writing one would keep a card in the
  // store forever after its last photo was removed.
  if (isEmptyDraft(draft)) return clearDraft(cardId);
  const record: StoredDraft = { cardId, ...draft, updatedAt: Date.now() };
  return run("readwrite", (store) => store.put(record));
}

export function clearDraft(cardId: number): Promise<unknown> {
  return run("readwrite", (store) => store.delete(cardId));
}

/**
 * Drop drafts for cards that are no longer in play.
 *
 * Called with the ids currently in hand and on hold, so a card that was completed,
 * thrown away or swept up by a category veto takes its draft with it. Never call
 * this without a loaded visit: an empty keep-list means "nothing is live", which
 * would clear a draft the player is still working on.
 */
export function pruneDrafts(keep: number[]): Promise<unknown> {
  const live = new Set(keep);
  return run<IDBValidKey[]>("readonly", (store) => store.getAllKeys()).then((keys) => {
    if (!keys) return;
    return Promise.all(
      keys
        .filter((key): key is number => typeof key === "number" && !live.has(key))
        .map(clearDraft),
    );
  });
}
